import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Layout
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import FeedbackWidget from "./components/layout/FeedbackWidget";
import PageVisitTracker from "./components/layout/PageVisitTracker";
import CanonicalLink from "./components/layout/CanonicalLink";
import ScrollToTop from "./components/layout/ScrollToTop";

// Pages
import ArticlesPage from "./pages/ArticlesPage";
import ArticlesSectionPage from "./pages/ArticlesSectionPage";
import ArticlesMoreArticlesPage from "./pages/ArticlesMoreArticlesPage";
import DataDrivenArticlePage from "./components/articles/DataDrivenArticlePage";
import SystemEngineeringProcessTechnicalArticlePage from "./pages/SystemEngineeringProcessTechnicalArticlePage";
import CybersecurityProcessTechnicalArticlePage from "./pages/CybersecurityProcessTechnicalArticlePage";
import FunctionalSafetyProcessTechnicalArticlePage from "./pages/FunctionalSafetyProcessTechnicalArticlePage";
import McuTractionInverterTechnicalArticlePage from "./pages/McuTractionInverterTechnicalArticlePage";
import BmsTechnicalArticlePage from "./pages/BmsTechnicalArticlePage";
import DcDcConverterEcuTechnicalArticlePage from "./pages/DcDcConverterEcuTechnicalArticlePage";
import HomePage from "./pages/HomePage";
import HowItWorksPage from "./pages/HowItWorksPage";
import HowItWorksTopicPage from "./pages/HowItWorksTopicPage";
import HowItWorksSectionPage from "./pages/HowItWorksSectionPage";
import HowItWorksMoreTopicsPage from "./pages/HowItWorksMoreTopicsPage";
import CommunityPage from "./pages/CommunityPage";
import QuestionDetailPage from "./pages/QuestionDetailPage";
import SearchPage from "./pages/SearchPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProfilePage from "./pages/ProfilePage";
import DashboardPage from "./pages/DashboardPage";
import AdminDashboard from "./pages/AdminDashboard";
import ToolsPage from "./pages/ToolsPage";
import AutosarE2EToolPage from "./pages/AutosarE2EToolPage";
import CrcCalculatorPage from "./pages/CrcCalculatorPage";
import DataIdCalculatorPage from "./pages/DataIdCalculatorPage";
import DataConverterPage from "./pages/DataConverterPage";
import EvBatterySizingPage from "./pages/EvBatterySizingPage";
import EvMotorSizingPage from "./pages/EvMotorSizingPage";
import TractionPowerPage from "./pages/TractionPowerPage";
import BrakingDistanceCalculatorPage from "./pages/BrakingDistanceCalculatorPage";
import GradientForceCalculatorPage from "./pages/GradientForceCalculatorPage";
import RollingResistanceCalculatorPage from "./pages/RollingResistanceCalculatorPage";
import AerodynamicForceCalculatorPage from "./pages/AerodynamicForceCalculatorPage";
import VehicleSpeedRpmPage from "./pages/VehicleSpeedRpmPage";
import GearRatioCalculatorPage from "./pages/GearRatioCalculatorPage";
import TorquePowerRpmCalculatorPage from "./pages/TorquePowerRpmCalculatorPage";
import TyreSizeCalculatorPage from "./pages/TyreSizeCalculatorPage";
import PowerSplitCalculatorPage from "./pages/PowerSplitCalculatorPage";
import RotationalSpeedConverterPage from "./pages/RotationalSpeedConverterPage";
import KwHpConverterPage from "./pages/KwHpConverterPage";
import TorqueConverterPage from "./pages/TorqueConverterPage";
import PressureConverterPage from "./pages/PressureConverterPage";
import SpeedConverterPage from "./pages/SpeedConverterPage";
import TemperatureConverterPage from "./pages/TemperatureConverterPage";
import FuelConsumptionConverterPage from "./pages/FuelConsumptionConverterPage";
import CanBusLoadCalculatorPage from "./pages/CanBusLoadCalculatorPage";
import CanEndiannessVisualizerPage from "./pages/CanEndiannessVisualizerPage";
import CanFrameDecoderPage from "./pages/CanFrameDecoderPage";
import J1939FrameDecoderPage from "./pages/J1939FrameDecoderPage";
import CableSizeCalculatorPage from "./pages/CableSizeCalculatorPage";
import FuseRatingCalculatorPage from "./pages/FuseRatingCalculatorPage";
import VoltageDropCalculatorPage from "./pages/VoltageDropCalculatorPage";
import PrechargeResistorCalculatorPage from "./pages/PrechargeResistorCalculatorPage";
import BusbarSizingCalculatorPage from "./pages/BusbarSizingCalculatorPage";
import DcDcConverterSizingCalculatorPage from "./pages/DcDcConverterSizingCalculatorPage";
import EvPerformanceRangeSimulatorPage from "./pages/EvPerformanceRangeSimulatorPage";
import UdsSimulatorPage from "./pages/UdsSimulatorPage";
import SecurityAccessSimulatorPage from "./pages/SecurityAccessSimulatorPage";
import NrcDecoderPage from "./pages/NrcDecoderPage";
import DtcDecoderPage from "./pages/DtcDecoderPage";
import SimulationPage from "./pages/SimulationPage";
import ChargingSimulatorPage from "./pages/ChargingSimulatorPage";
import RegenBrakingSimulatorPage from "./pages/RegenBrakingSimulatorPage";
import BatteryThermalSimulatorPage from "./pages/BatteryThermalSimulatorPage";
import HevEnergyManagementSimulatorPage from "./pages/HevEnergyManagementSimulatorPage";
import ComingSoon from "./pages/ComingSoon";

// Tool User Guides
import EvBatterySizingGuidePage from "./pages/guides/EvBatterySizingGuidePage";
import EvMotorSizingGuidePage from "./pages/guides/EvMotorSizingGuidePage";
import TractionPowerGuidePage from "./pages/guides/TractionPowerGuidePage";
import BrakingDistanceCalculatorGuidePage from "./pages/guides/BrakingDistanceCalculatorGuidePage";
import GradientForceCalculatorGuidePage from "./pages/guides/GradientForceCalculatorGuidePage";
import RollingResistanceCalculatorGuidePage from "./pages/guides/RollingResistanceCalculatorGuidePage";
import AerodynamicForceCalculatorGuidePage from "./pages/guides/AerodynamicForceCalculatorGuidePage";
import VehicleSpeedRpmGuidePage from "./pages/guides/VehicleSpeedRpmGuidePage";
import GearRatioCalculatorGuidePage from "./pages/guides/GearRatioCalculatorGuidePage";
import TorquePowerRpmCalculatorGuidePage from "./pages/guides/TorquePowerRpmCalculatorGuidePage";
import TyreSizeCalculatorGuidePage from "./pages/guides/TyreSizeCalculatorGuidePage";
import PowerSplitCalculatorGuidePage from "./pages/guides/PowerSplitCalculatorGuidePage";
import RotationalSpeedConverterGuidePage from "./pages/guides/RotationalSpeedConverterGuidePage";
import KwHpConverterGuidePage from "./pages/guides/KwHpConverterGuidePage";
import TorqueConverterGuidePage from "./pages/guides/TorqueConverterGuidePage";
import PressureConverterGuidePage from "./pages/guides/PressureConverterGuidePage";
import SpeedConverterGuidePage from "./pages/guides/SpeedConverterGuidePage";
import TemperatureConverterGuidePage from "./pages/guides/TemperatureConverterGuidePage";
import FuelConsumptionConverterGuidePage from "./pages/guides/FuelConsumptionConverterGuidePage";
import CanBusLoadCalculatorGuidePage from "./pages/guides/CanBusLoadCalculatorGuidePage";
import CanEndiannessVisualizerGuidePage from "./pages/guides/CanEndiannessVisualizerGuidePage";
import CanFrameDecoderGuidePage from "./pages/guides/CanFrameDecoderGuidePage";
import J1939FrameDecoderGuidePage from "./pages/guides/J1939FrameDecoderGuidePage";
import CableSizeCalculatorGuidePage from "./pages/guides/CableSizeCalculatorGuidePage";
import FuseRatingCalculatorGuidePage from "./pages/guides/FuseRatingCalculatorGuidePage";
import VoltageDropCalculatorGuidePage from "./pages/guides/VoltageDropCalculatorGuidePage";
import PrechargeResistorCalculatorGuidePage from "./pages/guides/PrechargeResistorCalculatorGuidePage";
import BusbarSizingCalculatorGuidePage from "./pages/guides/BusbarSizingCalculatorGuidePage";
import DcDcConverterSizingCalculatorGuidePage from "./pages/guides/DcDcConverterSizingCalculatorGuidePage";
import AutosarE2EGuidePage from "./pages/guides/AutosarE2EGuidePage";
import CrcCalculatorGuidePage from "./pages/guides/CrcCalculatorGuidePage";
import DataIdCalculatorGuidePage from "./pages/guides/DataIdCalculatorGuidePage";
import DataConverterGuidePage from "./pages/guides/DataConverterGuidePage";
import EvPowertrainSimulatorGuidePage from "./pages/guides/EvPowertrainSimulatorGuidePage";
import UdsSimulatorGuidePage from "./pages/guides/UdsSimulatorGuidePage";
import SecurityAccessSimulatorGuidePage from "./pages/guides/SecurityAccessSimulatorGuidePage";
import NrcDecoderGuidePage from "./pages/guides/NrcDecoderGuidePage";
import DtcDecoderGuidePage from "./pages/guides/DtcDecoderGuidePage";
import ChargingSimulatorGuidePage from "./pages/guides/ChargingSimulatorGuidePage";
import RegenBrakingSimulatorGuidePage from "./pages/guides/RegenBrakingSimulatorGuidePage";
import BatteryThermalSimulatorGuidePage from "./pages/guides/BatteryThermalSimulatorGuidePage";
import HevEnergyManagementGuidePage from "./pages/guides/HevEnergyManagementGuidePage";

// Footer-linked pages
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import AccessibilityStatementPage from "./pages/AccessibilityStatementPage";
import BeginnersGuidePage from "./pages/BeginnersGuidePage";
import GlossaryPage from "./pages/GlossaryPage";
import RequestTopicPage from "./pages/RequestTopicPage";
import ContentGuidelinesPage from "./pages/ContentGuidelinesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import CareersPage from "./pages/CareersPage";
import AdminTopicRequestsPage from "./pages/AdminTopicRequestsPage";
import AdminFeedbackPage from "./pages/AdminFeedbackPage";
import AdminCommunityPage from "./pages/AdminCommunityPage";
import AdminNewsletterSubscribersPage from "./pages/AdminNewsletterSubscribersPage";
import AdminFeaturedArticlesPage from "./pages/AdminFeaturedArticlesPage";
import AdminHowItWorksPage from "./pages/AdminHowItWorksPage";
import AdminHowItWorksEditorPage from "./pages/AdminHowItWorksEditorPage";
import AdminContentLibraryPage from "./pages/AdminContentLibraryPage";
import AdminContentEditorPage from "./pages/AdminContentEditorPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminVisitsPage from "./pages/AdminVisitsPage";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, redirectMessage }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-chassis"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname, message: redirectMessage }}
      />
    );
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-chassis"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Matches LoginPage's own post-login redirect target so a stray re-render of this
    // guard (e.g. while the auth state and the router location settle together during
    // login) can't briefly send the user to /articles instead of where they meant to go.
    return <Navigate to={location.state?.from || '/'} replace />;
  }

  return children;
};

function AppContent() {
  return (
    <div className="min-h-screen bg-aluminum">
      <ScrollToTop />
      <PageVisitTracker />
      <CanonicalLink />
      <Header />
      <main>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/system-engineering-process" element={<SystemEngineeringProcessTechnicalArticlePage />} />
          <Route path="/articles/cybersecurity-process" element={<CybersecurityProcessTechnicalArticlePage />} />
          <Route path="/articles/functional-safety-process" element={<FunctionalSafetyProcessTechnicalArticlePage />} />
          <Route path="/articles/mcu-traction-inverter" element={<McuTractionInverterTechnicalArticlePage />} />
          <Route path="/articles/bms" element={<BmsTechnicalArticlePage />} />
          <Route path="/articles/dcdc-converter-ecu" element={<DcDcConverterEcuTechnicalArticlePage />} />
          {/* Old URLs shaped like the internal page_contents slug (/article-<name>), which Google indexed */}
          <Route path="/article-system-engineering-process" element={<Navigate to="/articles/system-engineering-process" replace />} />
          <Route path="/article-cybersecurity-process" element={<Navigate to="/articles/cybersecurity-process" replace />} />
          <Route path="/article-functional-safety-process" element={<Navigate to="/articles/functional-safety-process" replace />} />
          <Route path="/article-mcu-traction-inverter" element={<Navigate to="/articles/mcu-traction-inverter" replace />} />
          <Route path="/article-bms" element={<Navigate to="/articles/bms" replace />} />
          <Route path="/article-dcdc-converter-ecu" element={<Navigate to="/articles/dcdc-converter-ecu" replace />} />
          <Route path="/articles/section/:sectionSlug" element={<ArticlesSectionPage />} />
          <Route path="/articles/section/:sectionSlug/more" element={<ArticlesMoreArticlesPage />} />
          <Route path="/articles/reference/:slug" element={<DataDrivenArticlePage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          {/* EV Safety Systems was folded into Battery & Charging -> BMS on 2026-08-14, then the whole
              taxonomy was restructured to E-Powertrain & Drivetrain -> Battery & BMS on 2026-08-15
              per the Website_Domain_Wise_Topics.docx master taxonomy. HVIL's own permalink
              (/how-it-works/hvil) never depended on category, so only these section/browse URLs
              need a redirect. */}
          <Route path="/how-it-works/section/ev-safety-systems" element={<Navigate to="/how-it-works/section/e-powertrain-drivetrain/battery-bms" replace />} />
          <Route path="/how-it-works/section/ev-safety-systems/more" element={<Navigate to="/how-it-works/section/e-powertrain-drivetrain/battery-bms/more" replace />} />
          <Route path="/how-it-works/section/battery-charging/bms" element={<Navigate to="/how-it-works/section/e-powertrain-drivetrain/battery-bms" replace />} />
          <Route path="/how-it-works/section/battery-charging/bms/more" element={<Navigate to="/how-it-works/section/e-powertrain-drivetrain/battery-bms/more" replace />} />
          <Route path="/how-it-works/section/:categorySlug" element={<HowItWorksSectionPage />} />
          <Route path="/how-it-works/section/:categorySlug/more" element={<HowItWorksMoreTopicsPage />} />
          <Route path="/how-it-works/section/:categorySlug/:subcategorySlug" element={<HowItWorksSectionPage />} />
          <Route path="/how-it-works/section/:categorySlug/:subcategorySlug/more" element={<HowItWorksMoreTopicsPage />} />
          <Route path="/how-it-works/:topicId" element={<HowItWorksTopicPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/community/:questionId" element={<QuestionDetailPage />} />
          <Route path="/search" element={<SearchPage />} />

          {/* Auth Routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Protected User Routes */}
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

          {/* Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/topic-requests" element={<ProtectedRoute adminOnly><AdminTopicRequestsPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsersPage /></ProtectedRoute>} />
          <Route path="/admin/visits" element={<ProtectedRoute adminOnly><AdminVisitsPage /></ProtectedRoute>} />
          <Route path="/admin/feedback" element={<ProtectedRoute adminOnly><AdminFeedbackPage /></ProtectedRoute>} />
          <Route path="/admin/community" element={<ProtectedRoute adminOnly><AdminCommunityPage /></ProtectedRoute>} />
          <Route path="/admin/newsletter-subscribers" element={<ProtectedRoute adminOnly><AdminNewsletterSubscribersPage /></ProtectedRoute>} />
          <Route path="/admin/articles" element={<ProtectedRoute adminOnly><AdminFeaturedArticlesPage /></ProtectedRoute>} />
          <Route path="/admin/featured-articles" element={<Navigate to="/admin/articles" replace />} />
          <Route path="/admin/how-it-works" element={<ProtectedRoute adminOnly><AdminHowItWorksPage /></ProtectedRoute>} />
          <Route path="/admin/how-it-works/:topicId" element={<ProtectedRoute adminOnly><AdminHowItWorksEditorPage /></ProtectedRoute>} />
          <Route path="/admin/content" element={<ProtectedRoute adminOnly><AdminContentLibraryPage /></ProtectedRoute>} />
          <Route path="/admin/content/new/:contentType" element={<ProtectedRoute adminOnly><AdminContentEditorPage /></ProtectedRoute>} />
          <Route path="/admin/content/:slug" element={<ProtectedRoute adminOnly><AdminContentEditorPage /></ProtectedRoute>} />
          
          {/* Tools */}
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/tools/autosar-e2e" element={<AutosarE2EToolPage />} />
          <Route path="/tools/crc-calculator" element={<CrcCalculatorPage />} />
          <Route path="/tools/data-id-calculator" element={<DataIdCalculatorPage />} />
          <Route path="/tools/data-converter" element={<DataConverterPage />} />
          <Route path="/tools/ev-battery-sizing" element={<EvBatterySizingPage />} />
          <Route path="/tools/ev-motor-sizing" element={<EvMotorSizingPage />} />
          <Route path="/tools/traction-power" element={<TractionPowerPage />} />
          <Route path="/tools/braking-distance-calculator" element={<BrakingDistanceCalculatorPage />} />
          <Route path="/tools/gradient-force-calculator" element={<GradientForceCalculatorPage />} />
          <Route path="/tools/rolling-resistance-calculator" element={<RollingResistanceCalculatorPage />} />
          <Route path="/tools/aerodynamic-force-calculator" element={<AerodynamicForceCalculatorPage />} />
          <Route path="/tools/vehicle-speed-rpm" element={<VehicleSpeedRpmPage />} />
          <Route path="/tools/gear-ratio-calculator" element={<GearRatioCalculatorPage />} />
          <Route path="/tools/torque-power-rpm-calculator" element={<TorquePowerRpmCalculatorPage />} />
          <Route path="/tools/tyre-size-calculator" element={<TyreSizeCalculatorPage />} />
          <Route path="/tools/powertrain/power-split-calculator" element={<PowerSplitCalculatorPage />} />
          <Route path="/tools/rpm-rad-converter" element={<RotationalSpeedConverterPage />} />
          <Route path="/tools/kw-hp-calculator" element={<KwHpConverterPage />} />
          <Route path="/tools/torque-calculator" element={<TorqueConverterPage />} />
          <Route path="/tools/pressure-calculator" element={<PressureConverterPage />} />
          <Route path="/tools/speed-calculator" element={<SpeedConverterPage />} />
          <Route path="/tools/temperature-calculator" element={<TemperatureConverterPage />} />
          <Route path="/tools/fuel-consumption-calculator" element={<FuelConsumptionConverterPage />} />
          <Route path="/tools/can-bus-load-calculator" element={<CanBusLoadCalculatorPage />} />
          <Route path="/tools/can-endianness-visualizer" element={<CanEndiannessVisualizerPage />} />
          <Route path="/tools/can-frame-decoder" element={<CanFrameDecoderPage />} />
          <Route path="/tools/j1939-frame-decoder" element={<J1939FrameDecoderPage />} />
          <Route path="/tools/cable-size-calculator" element={<CableSizeCalculatorPage />} />
          <Route path="/tools/power-electronics/fuse-rating-calculator" element={<FuseRatingCalculatorPage />} />
          <Route path="/tools/voltage-drop-calculator" element={<VoltageDropCalculatorPage />} />
          <Route path="/tools/precharge-resistor-calculator" element={<PrechargeResistorCalculatorPage />} />
          <Route path="/tools/busbar-sizing-calculator" element={<BusbarSizingCalculatorPage />} />
          <Route path="/tools/dcdc-converter-sizing-calculator" element={<DcDcConverterSizingCalculatorPage />} />
          <Route path="/tools/diagnostic/uds-simulator" element={<UdsSimulatorPage />} />
          <Route path="/tools/diagnostic/security-access-simulator" element={<SecurityAccessSimulatorPage />} />
          <Route path="/tools/diagnostic/nrc-decoder" element={<NrcDecoderPage />} />
          <Route path="/tools/diagnostic/dtc-decoder" element={<DtcDecoderPage />} />

          {/* Tool User Guides */}
          <Route path="/tools/autosar-e2e/guide" element={<AutosarE2EGuidePage />} />
          <Route path="/tools/crc-calculator/guide" element={<CrcCalculatorGuidePage />} />
          <Route path="/tools/data-id-calculator/guide" element={<DataIdCalculatorGuidePage />} />
          <Route path="/tools/data-converter/guide" element={<DataConverterGuidePage />} />
          <Route path="/tools/ev-battery-sizing/guide" element={<EvBatterySizingGuidePage />} />
          <Route path="/tools/ev-motor-sizing/guide" element={<EvMotorSizingGuidePage />} />
          <Route path="/tools/traction-power/guide" element={<TractionPowerGuidePage />} />
          <Route path="/tools/braking-distance-calculator/guide" element={<BrakingDistanceCalculatorGuidePage />} />
          <Route path="/tools/gradient-force-calculator/guide" element={<GradientForceCalculatorGuidePage />} />
          <Route path="/tools/rolling-resistance-calculator/guide" element={<RollingResistanceCalculatorGuidePage />} />
          <Route path="/tools/aerodynamic-force-calculator/guide" element={<AerodynamicForceCalculatorGuidePage />} />
          <Route path="/tools/vehicle-speed-rpm/guide" element={<VehicleSpeedRpmGuidePage />} />
          <Route path="/tools/gear-ratio-calculator/guide" element={<GearRatioCalculatorGuidePage />} />
          <Route path="/tools/torque-power-rpm-calculator/guide" element={<TorquePowerRpmCalculatorGuidePage />} />
          <Route path="/tools/tyre-size-calculator/guide" element={<TyreSizeCalculatorGuidePage />} />
          <Route path="/tools/powertrain/power-split-calculator/guide" element={<PowerSplitCalculatorGuidePage />} />
          <Route path="/tools/rpm-rad-converter/guide" element={<RotationalSpeedConverterGuidePage />} />
          <Route path="/tools/kw-hp-calculator/guide" element={<KwHpConverterGuidePage />} />
          <Route path="/tools/torque-calculator/guide" element={<TorqueConverterGuidePage />} />
          <Route path="/tools/pressure-calculator/guide" element={<PressureConverterGuidePage />} />
          <Route path="/tools/speed-calculator/guide" element={<SpeedConverterGuidePage />} />
          <Route path="/tools/temperature-calculator/guide" element={<TemperatureConverterGuidePage />} />
          <Route path="/tools/fuel-consumption-calculator/guide" element={<FuelConsumptionConverterGuidePage />} />
          <Route path="/tools/can-bus-load-calculator/guide" element={<CanBusLoadCalculatorGuidePage />} />
          <Route path="/tools/can-endianness-visualizer/guide" element={<CanEndiannessVisualizerGuidePage />} />
          <Route path="/tools/can-frame-decoder/guide" element={<CanFrameDecoderGuidePage />} />
          <Route path="/tools/j1939-frame-decoder/guide" element={<J1939FrameDecoderGuidePage />} />
          <Route path="/tools/cable-size-calculator/guide" element={<CableSizeCalculatorGuidePage />} />
          <Route path="/tools/power-electronics/fuse-rating-calculator/guide" element={<FuseRatingCalculatorGuidePage />} />
          <Route path="/tools/voltage-drop-calculator/guide" element={<VoltageDropCalculatorGuidePage />} />
          <Route path="/tools/precharge-resistor-calculator/guide" element={<PrechargeResistorCalculatorGuidePage />} />
          <Route path="/tools/busbar-sizing-calculator/guide" element={<BusbarSizingCalculatorGuidePage />} />
          <Route path="/tools/dcdc-converter-sizing-calculator/guide" element={<DcDcConverterSizingCalculatorGuidePage />} />
          <Route path="/tools/diagnostic/uds-simulator/guide" element={<UdsSimulatorGuidePage />} />
          <Route path="/tools/diagnostic/security-access-simulator/guide" element={<SecurityAccessSimulatorGuidePage />} />
          <Route path="/tools/diagnostic/nrc-decoder/guide" element={<NrcDecoderGuidePage />} />
          <Route path="/tools/diagnostic/dtc-decoder/guide" element={<DtcDecoderGuidePage />} />

          {/* Simulation */}
          <Route path="/simulation" element={<SimulationPage />} />
          <Route path="/simulation/ev-powertrain" element={<EvPerformanceRangeSimulatorPage />} />
          <Route path="/simulation/ev-powertrain/guide" element={<EvPowertrainSimulatorGuidePage />} />
          <Route path="/simulation/charging" element={<ChargingSimulatorPage />} />
          <Route path="/simulation/charging/guide" element={<ChargingSimulatorGuidePage />} />
          <Route path="/simulation/regen-braking" element={<RegenBrakingSimulatorPage />} />
          <Route path="/simulation/regen-braking/guide" element={<RegenBrakingSimulatorGuidePage />} />
          <Route path="/simulation/battery-thermal" element={<BatteryThermalSimulatorPage />} />
          <Route path="/simulation/battery-thermal/guide" element={<BatteryThermalSimulatorGuidePage />} />
          <Route path="/simulation/hev-energy-management" element={<HevEnergyManagementSimulatorPage />} />
          <Route path="/simulation/hev-energy-management/guide" element={<HevEnergyManagementGuidePage />} />

          {/* Coming Soon Pages */}
          <Route path="/courses" element={<ComingSoon title="Courses" />} />

          {/* Resources */}
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/beginners-guide" element={<BeginnersGuidePage />} />
          <Route
            path="/request-topic"
            element={
              <ProtectedRoute redirectMessage="Log in to request a topic.">
                <RequestTopicPage />
              </ProtectedRoute>
            }
          />

          {/* Community */}
          {/* /forum was a static category placeholder; categories now live on /community itself. */}
          <Route path="/forum" element={<Navigate to="/community" replace />} />
          <Route path="/content-guidelines" element={<ContentGuidelinesPage />} />

          {/* Company */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/careers" element={<CareersPage />} />

          {/* Legal */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/accessibility" element={<AccessibilityStatementPage />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <FeedbackWidget />
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
